// Auth-state машина: anonymous → challenge → authenticated.
// Состояние держим явно, не дёргаем внутренний кеш TanStack Query
// для логики переходов (он — для данных, а это — для UI-flow).

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { ApiError, api } from '../lib/api.ts';
import type { User } from '../lib/types.ts';

type AuthState =
  | { kind: 'loading' }
  | { kind: 'anonymous'; error?: string }
  | { kind: 'challenge'; error?: string }
  | { kind: 'authenticated'; user: User };

interface AuthContextValue {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  submitTotp: (code: string) => Promise<void>;
  submitRecovery: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Вызывается из useMe при потере сессии (401). */
  forceAnonymous: () => void;
  /** Заполняется из useMe-запроса при старте/инвалидации. */
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ kind: 'loading' });

  const setUser = useCallback((user: User | null) => {
    if (user) setState({ kind: 'authenticated', user });
    else setState((prev) => (prev.kind === 'loading' ? { kind: 'anonymous' } : prev));
  }, []);

  const forceAnonymous = useCallback(() => {
    setState({ kind: 'anonymous' });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      await api.post<{ totp_required: true }>('/auth/login', { email, password });
      setState({ kind: 'challenge' });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Не удалось войти';
      setState({ kind: 'anonymous', error: msg });
      throw err;
    }
  }, []);

  const submitTotp = useCallback(async (code: string) => {
    try {
      const { user } = await api.post<{ user: User }>('/auth/totp', { totp_code: code });
      setState({ kind: 'authenticated', user });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Неверный код';
      setState((prev) => (prev.kind === 'challenge' ? { kind: 'challenge', error: msg } : prev));
      throw err;
    }
  }, []);

  const submitRecovery = useCallback(async (code: string) => {
    try {
      const { user } = await api.post<{ user: User }>('/auth/totp', { recovery_code: code });
      setState({ kind: 'authenticated', user });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Неверный recovery-код';
      setState((prev) => (prev.kind === 'challenge' ? { kind: 'challenge', error: msg } : prev));
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // даже если сервер вернул ошибку, локально уходим в anonymous
    }
    setState({ kind: 'anonymous' });
  }, []);

  return (
    <AuthContext.Provider
      value={{ state, login, submitTotp, submitRecovery, logout, forceAnonymous, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
